/**
 * Manufacturer identity, mirroring the `Manufacturer` union in
 * backend/src/services/workflow/manufacturer.ts. Keep in sync by hand — gate
 * UI on the API's normalized `manufacturerSlug` rather than comparing raw
 * Monday labels.
 */

export const Manufacturer = {
    Spark: 'spark',
    Invisalign: 'invisalign'
} as const;
export type Manufacturer = (typeof Manufacturer)[keyof typeof Manufacturer];

/**
 * Intentional strict match, not `!= Manufacturer.Invisalign`: the backend
 * treats a null Aligner Preference as Spark-automatable, but this pre-existing
 * frontend check does not mirror that — leave it as-is, this is not a bug.
 */
export function isSparkManufacturer(slug: Manufacturer | null): boolean {
    return slug === Manufacturer.Spark;
}

const DISPLAY_NAME: Record<Manufacturer, string> = {
    [Manufacturer.Spark]: 'Spark',
    [Manufacturer.Invisalign]: 'Invisalign'
};

/** Brand name of a known manufacturer. */
export function manufacturerBrandName(manufacturer: Manufacturer): string {
    return DISPLAY_NAME[manufacturer];
}

/**
 * Who the account returned by `accountFor` belongs to. An unknown manufacturer
 * reads as Spark because `accountFor` resolves the Spark account in that case —
 * the label has to name the account the UI is actually offering. Deliberately
 * not the backend's `manufacturerDisplayName`, which reads a null manufacturer
 * as 'the manufacturer' because it has no account to name.
 */
export function accountOwnerLabel(manufacturer: Manufacturer | null | undefined): string {
    return DISPLAY_NAME[manufacturer ?? Manufacturer.Spark];
}

/**
 * The practice's account with the case's manufacturer.
 *
 * A practice can hold both a Spark and an Invisalign account, so this must
 * follow the case's manufacturer — showing the other one would have an operator
 * working in the wrong portal. An unknown manufacturer falls back to Spark, the
 * same permissive default the backend applies.
 */
export function accountFor(
    practice: { sparkAcct: string | null; invisalignAcct: string | null } | null | undefined,
    manufacturer: Manufacturer | null | undefined
): string | null {
    if (!practice) return null;
    const account =
        manufacturer === Manufacturer.Invisalign ? practice.invisalignAcct : practice.sparkAcct;
    // Return the trimmed value, not the raw one: this string is copied to the
    // operator's clipboard for a portal login, where padding won't match.
    const trimmed = account?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
}
